import { toISODate } from "@rumbledirect/ui-v2/day";

/** How far ahead the monitor looks when the user has not picked a period yet. */
const DEFAULT_WINDOW_MONTHS = 12;

/**
 * The inclusive window a contract has to overlap to show up in the monitor.
 *
 * Both bounds are required by the backend: without a period the question
 * "which contracts occupy the fleet" has no answer, so there is no unbounded
 * default to fall back on.
 */
export interface ContractMonitorPeriod {
  readonly activeFrom: string;
  readonly activeUntil: string;
}

/**
 * The period the monitor opens on: from today up to a year ahead, which is the
 * horizon a fleet manager plans replacements over.
 *
 * `today` is injectable so callers (and tests) can pin the window instead of
 * depending on the clock.
 */
export function defaultContractMonitorPeriod(
  today: Date = new Date(),
): ContractMonitorPeriod {
  const until = new Date(
    today.getFullYear(),
    today.getMonth() + DEFAULT_WINDOW_MONTHS,
    today.getDate(),
  );

  return { activeFrom: toISODate(today), activeUntil: toISODate(until) };
}

/**
 * Whether the period reads forwards. `YYYY-MM-DD` sorts chronologically as a
 * string, so no date parsing is needed.
 *
 * An inverted period is a user mistake, not a query: the backend would answer
 * it with a 400, so the UI keeps the request unsent and says what is wrong.
 */
export function isPeriodOrdered(period: ContractMonitorPeriod): boolean {
  return period.activeFrom <= period.activeUntil;
}
